import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Mascot } from '@/components/Mascot';
import { usePreferredSport } from '@/hooks/usePreferredSport';

interface CelebrationOverlayProps {
  show: boolean;
  xp: number;
  gems: number;
  isPerfect: boolean;
}

const CONFETTI_COLORS = [
  'hsl(210 100% 50%)',  // primary blue
  'hsl(35 95% 55%)',    // accent amber
  'hsl(0 72% 51%)',     // destructive
  'hsl(160 60% 45%)',   // success
  'hsl(280 80% 60%)',   // purple
];

function ConfettiPiece({ delay, color }: { delay: number; color: string }) {
  const x = Math.random() * 100;
  const rotation = Math.random() * 360;
  const size = 6 + Math.random() * 6;

  return (
    <motion.div
      className="absolute top-0 rounded-sm"
      style={{
        left: `${x}%`,
        width: size,
        height: size * 0.6,
        backgroundColor: color,
      }}
      initial={{ y: -20, opacity: 1, rotate: 0, scale: 0 }}
      animate={{
        y: [0, 300 + Math.random() * 200],
        opacity: [1, 1, 0],
        rotate: rotation + 720,
        scale: [0, 1, 0.5],
        x: [0, (Math.random() - 0.5) * 100],
      }}
      transition={{
        duration: 1.5 + Math.random() * 0.5,
        delay: delay,
        ease: 'easeOut',
      }}
    />
  );
}

export function CelebrationOverlay({ show, xp, gems, isPerfect }: CelebrationOverlayProps) {
  const { preferredSport } = usePreferredSport();
  const [particles, setParticles] = useState<{ id: number; delay: number; color: string }[]>([]);

  useEffect(() => {
    if (show) {
      const newParticles = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        delay: Math.random() * 0.3,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      }));
      setParticles(newParticles);
    }
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 pointer-events-none overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Confetti */}
          {particles.map(p => (
            <ConfettiPiece key={p.id} delay={p.delay} color={p.color} />
          ))}

          {/* Center burst */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="flex flex-col items-center gap-3"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 1] }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Mascot mood="celebration" sport={preferredSport} size={80} />

              {isPerfect && (
                <motion.div
                  className="text-4xl"
                  animate={{ rotate: [0, -10, 10, -5, 5, 0] }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  🏆
                </motion.div>
              )}

              <motion.div
                className="flex items-center gap-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="bg-primary/90 backdrop-blur rounded-xl px-4 py-2 flex items-center gap-2 shadow-glow-primary">
                  <span className="text-lg">⚡</span>
                  <span className="display-number text-lg text-primary-foreground">+{xp} XP</span>
                </div>
                {gems > 0 && (
                  <div className="bg-accent/90 backdrop-blur rounded-xl px-4 py-2 flex items-center gap-2 shadow-glow-accent">
                    <span className="text-lg">💎</span>
                    <span className="display-number text-lg text-accent-foreground">+{gems}</span>
                  </div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
